import type { SidePreparation } from '../lib/verificationItems.ts'
import { lineKind } from '../lib/lineKind.ts'
import {
  formatDay,
  formatNumber,
  lineFactors,
  type ProductionSheet as Sheet,
} from '../lib/productionSheet.ts'

/**
 * A note behind an arrow, closed (Galdino, 2026-09-29): printed open under
 * every line, the notes crowded the ficha out. Opened, it is in full with its
 * own line breaks, and a long one scrolls inside its box rather than pushing
 * the table away.
 */
function FoldedNote({ label, text }: { label: string; text: string | undefined }) {
  if (text === undefined) return null
  return (
    <details className="mt-1">
      <summary className="text-text-weak hover:text-text-strong cursor-pointer select-none">
        {label}
      </summary>
      <span className="border-stroke-weak text-text-strong mt-1 block max-h-48 overflow-y-auto border-l-2 pl-2 break-words whitespace-pre-wrap">
        {text}
      </span>
    </details>
  )
}

/**
 * The ficha de produção of one item, the way Formula Certa's "Ordem de
 * manipulação" prints it: the pote's packaging and register number, then
 * every line the ERP keyed -- the excipient, the shell, the pot included,
 * which the pairing deliberately ignores -- typed, with what is weighed and
 * the pharmacy's observação on the product.
 *
 * Lot, expiry and supplier are left out on purpose (meeting of 2026-09-23,
 * #467): the person weighing on the floor reads them, the pharmacist who
 * confers does not. What the pharmacist does read is the observação --
 * "se em microgramas usar código X", "se espécie 2 usar bisnaga" -- so it is
 * printed in full under the product, never cropped.
 *
 * Collapsed: it is what the pharmacist would otherwise have on paper at the
 * bench, reference rather than a comparison. Nothing in it is scored, so it
 * carries no colour.
 */
export function ProductionSheet({
  preparation,
  sheet,
}: {
  preparation: SidePreparation | undefined
  sheet: Sheet | undefined
}) {
  // Never read at all: an older conferência, or a simulation. Nothing to say.
  if (sheet === undefined || preparation === undefined) return null

  if (!sheet.available)
    return (
      <p className="text-tSmall text-text-weak mt-4">
        A ficha de produção não pôde ser lida do ERP para esta conferência.
      </p>
    )

  const lines = preparation.ingredients
  if (sheetFacts(preparation, sheet).length === 0 && lines.length === 0) return null

  return (
    <details className="border-stroke-weak mt-4 rounded-base border px-4 py-3">
      <summary className="text-tSmall text-text-weak cursor-pointer select-none">
        Ficha de produção
        {lines.length > 0 && ` (${lines.length} ${lines.length === 1 ? 'linha' : 'linhas'})`}
        {preparation.form !== undefined && ` · ${preparation.form}`}
        {preparation.quantity !== undefined &&
          ` · ${preparation.quantity.amount} ${preparation.quantity.unit}`}
      </summary>
      <ProductionSheetContent preparation={preparation} sheet={sheet} />
    </details>
  )
}

function sheetFacts(preparation: SidePreparation, sheet: Sheet) {
  const own = sheet.preparations.find((one) => one.preparationId === preparation.id)
  return [
    { label: 'Embalagem', value: own?.packaging },
    { label: 'Validade', value: formatDay(own?.validUntil) },
    { label: 'N.º registro', value: own?.registration },
    {
      label: 'Sobrecarga',
      value: own?.overfill === undefined ? undefined : formatNumber(own.overfill),
    },
  ].filter((fact): fact is { label: string; value: string } => fact.value !== undefined)
}

/**
 * The production sheet without the fold around it: for the divergence-only pass, which
 * folds every preparation's into one section on a step not about one (#641).
 */
export function ProductionSheetContent({
  preparation,
  sheet,
}: {
  preparation: SidePreparation
  sheet: Sheet
}) {
  if (!sheet.available)
    return (
      <p className="text-tSmall text-text-weak mt-2">
        A ficha de produção não pôde ser lida do ERP para esta conferência.
      </p>
    )

  const own = sheet.preparations.find((one) => one.preparationId === preparation.id)
  const lineOf = new Map((own?.ingredients ?? []).map((line) => [line.ingredientId, line]))
  const facts = sheetFacts(preparation, sheet)
  const lines = preparation.ingredients

  return (
    <>
      {facts.length > 0 && (
        <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
          {facts.map((fact) => (
            <div key={fact.label} className="flex gap-1.5">
              <dt className="text-tSmall text-text-weak">{fact.label}</dt>
              <dd className="text-tSmall text-text-strong">{fact.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {lines.length > 0 && (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="text-tSmall text-text-weak text-left">
                <th className="py-1.5 pr-3 font-normal">Item</th>
                <th className="py-1.5 pr-3 font-normal">Tipo</th>
                {/* QTREAL: the product weighed for the whole requisição, in
                    its stock unit, dilution and equivalence already applied --
                    so 4 MG/ML over 60 ML reads 0,24 G, and a 1:10 trituration
                    ten times its dose (#467). */}
                <th
                  className="py-1.5 text-right font-normal"
                  title="Quanto se pesa do produto para a requisição inteira, já com diluição e equivalência"
                >
                  Total a pesar
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((ingredient) => {
                const line = lineOf.get(ingredient.id) ?? { ingredientId: ingredient.id }
                const factors = lineFactors(line)
                const observation = line.observation?.trim() || undefined
                return (
                  <tr key={ingredient.id} className="border-stroke-weak border-t align-top">
                    <td
                      className={`text-tSmall py-1.5 pr-3 ${ingredient.kind === 'active' || ingredient.kind === undefined ? 'text-text-strong' : 'text-text-weak'}`}
                    >
                      {ingredient.rawName}
                      {ingredient.kind === 'associated' &&
                        ingredient.associatedWith !== undefined && (
                          <span className="text-text-weak">
                            {' '}
                            · associado a {ingredient.associatedWith}
                          </span>
                        )}
                      {factors.length > 0 && (
                        <span className="text-text-weak block">{factors.join(' · ')}</span>
                      )}
                      {/*
                        Named after the ERP grid it comes from, "Observações
                        de Ficha" (#616). The product's ficha técnica note
                        (FC03900.OBS_FICHA) used to sit beside it; it is
                        registration and monograph text, and a pharmacist
                        reading it here took it for what the ficha says.
                      */}
                      <FoldedNote label="Observações da ficha" text={observation} />
                    </td>
                    <td className="text-tSmall text-text-weak py-1.5 pr-3 whitespace-nowrap">
                      {lineKind(ingredient)}
                    </td>
                    <td className="text-tSmall text-text-strong py-1.5 text-right tabular-nums whitespace-nowrap">
                      {line.weighed === undefined
                        ? '—'
                        : `${formatNumber(line.weighed.amount)} ${line.weighed.unit}`.trim()}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
